import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI } from '../lib/api';
import { formatCurrency, formatPercent, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, TrendingUp, TrendingDown, Target, 
  Calendar, DollarSign, RefreshCw, Award, AlertTriangle, Check, Trophy,
  Activity, Wallet, BarChart3, Clock, Shield
} from 'lucide-react';
import FloatingLossIndicator from '../components/FloatingLossIndicator';
import MatchTraderCredentials from '../components/MatchTraderCredentials';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, color = 'blue', trend }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    red: 'text-red-400 bg-red-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
    gray: 'text-gray-400 bg-white/5',
  };

  return (
    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            <p className={`text-2xl font-bold font-mono ${
              trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-white'
            }`}>{value}</p>
            {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ChallengeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeAmount, setTradeAmount] = useState('');
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    fetchChallenge();
  }, [id]);

  const fetchChallenge = async () => {
    try {
      const response = await challengesAPI.getChallengeDetail(id);
      setChallenge(response.data);
    } catch (error) {
      toast.error('Failed to load challenge');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateTrade = async (isProfit) => {
    const amount = parseFloat(tradeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSimulating(true);
    try {
      const profit = isProfit ? amount : -amount;
      const response = await challengesAPI.simulateTrade(id, profit);
      toast.success(response.data.message || `Trade simulated: ${formatCurrency(profit)}`);
      fetchChallenge();
      setTradeAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to simulate trade');
    } finally {
      setSimulating(false);
    }
  };

  const handleRequestRetake = async () => {
    try {
      await challengesAPI.requestRetake(id);
      toast.success('Free retake activated! Good luck!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request retake');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!challenge) return null;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 font-medium">ACTIVE</Badge>;
      case 'passed':
      case 'passed_phase1':
        return <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 font-medium">PASSED</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 font-medium">FAILED</Badge>;
      case 'funded':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-medium">FUNDED</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 font-medium">{status?.toUpperCase()}</Badge>;
    }
  };

  const profitTarget = challenge.current_phase === 1 ? 8 : 5;
  const progressToTarget = (challenge.profit_loss_percent / profitTarget) * 100;
  const drawdownUsed = challenge.profit_loss < 0 ? (Math.abs(challenge.profit_loss) / (challenge.starting_balance * 0.1)) * 100 : 0;

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="challenge-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-6 border-white/10 text-white hover:bg-white/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white" data-testid="challenge-title">
                  {challenge.package_name}
                </h1>
                {getStatusBadge(challenge.status)}
              </div>
              <div className="flex items-center gap-4 text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Started {formatDate(challenge.start_date)}
                </span>
                {challenge.is_free_retake && (
                  <Badge variant="outline" className="border-green-500/30 text-green-400">
                    <RefreshCw className="w-3 h-3 mr-1" />
                    FREE RETAKE
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              {(challenge.status === 'active' || challenge.status === 'passed_phase1') && (
                <Button 
                  onClick={() => navigate(`/trade/${id}`)}
                  className="font-heading uppercase tracking-wider bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg px-6 shadow-lg shadow-green-500/25"
                  data-testid="start-trading-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {challenge.status === 'passed_phase1' ? 'CONTINUE TO PHASE 2' : 'START TRADING'}
                </Button>
              )}
              {challenge.status === 'funded' && (
                <Button 
                  onClick={() => navigate('/payouts')}
                  className="font-heading uppercase tracking-wider bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg px-6 shadow-lg shadow-blue-500/25"
                  data-testid="request-payout-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  REQUEST PAYOUT
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Phase 2 Completion Banner - Ready for Funding */}
        {challenge.status === 'active' && challenge.current_phase === 2 && challenge.profit_loss_percent >= 5 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-green-500/20 to-cyan-500/20 border border-green-500/30"
            data-testid="phase2-complete-banner"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Award className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-white">🎉🎉 CONGRATULATIONS! 🎉🎉</h3>
                  <p className="text-gray-300">You have PASSED Phase 2! You're now eligible for a FUNDED ACCOUNT!</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Your trading skills have been verified. Welcome to the funded trader program!
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_URL}/api/challenges/${id}/become-funded`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                      toast.success('🎉 Congratulations! Your funded account is now active!');
                      fetchChallenge();
                    } else {
                      const data = await response.json();
                      toast.error(data.detail || 'Failed to activate funded account');
                    }
                  } catch (error) {
                    toast.error('Failed to activate funded account');
                  }
                }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-heading uppercase tracking-wider px-8 py-3 rounded-lg shadow-lg"
                data-testid="become-funded-btn"
              >
                <Award className="w-4 h-4 mr-2" />
                ACTIVATE FUNDED ACCOUNT
              </Button>
            </div>
          </motion.div>
        )}

        {/* Phase 1 Completion Banner */}
        {challenge.status === 'passed_phase1' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-white">🎉 Congratulations!</h3>
                  <p className="text-gray-300">You have successfully completed Phase 1! You are now ready for Phase 2.</p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_URL}/api/challenges/${id}/start-phase2`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                      toast.success('Phase 2 started! Good luck!');
                      fetchChallenge();
                    } else {
                      const data = await response.json();
                      toast.error(data.detail || 'Failed to start Phase 2');
                    }
                  } catch (error) {
                    toast.error('Failed to start Phase 2');
                  }
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-heading uppercase tracking-wider px-8 py-3 rounded-lg shadow-lg"
                data-testid="start-phase2-btn"
              >
                START PHASE 2 →
              </Button>
            </div>
          </motion.div>
        )}

        {/* Failed Banner with Retake Option */}
        {challenge.status === 'failed' && challenge.free_retake_available && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 rounded-2xl bg-red-500/10 border border-red-500/30"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-white">Challenge Failed</h3>
                  <p className="text-gray-300">Don't worry! You have a FREE RETAKE available.</p>
                </div>
              </div>
              <Button 
                onClick={handleRequestRetake}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase tracking-wider px-6 rounded-lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                USE FREE RETAKE
              </Button>
            </div>
          </motion.div>
        )}

        {/* Main Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <StatCard 
            title="Current Balance" 
            value={formatCurrency(challenge.current_balance)}
            subValue={`Started: ${formatCurrency(challenge.starting_balance)}`}
            icon={DollarSign}
            color="cyan"
          />
          <StatCard 
            title="Profit/Loss" 
            value={formatPercent(challenge.profit_loss_percent)}
            subValue={formatCurrency(challenge.profit_loss)}
            icon={challenge.profit_loss >= 0 ? TrendingUp : TrendingDown}
            color={challenge.profit_loss >= 0 ? 'green' : 'red'}
            trend={challenge.profit_loss >= 0 ? 'up' : 'down'}
          />
          <StatCard 
            title="Current Phase" 
            value={`Phase ${challenge.current_phase}`}
            subValue={challenge.package_type === 'instant' ? 'Instant Funding' : `Target: ${profitTarget}%`}
            icon={Target}
            color="blue"
          />
          <StatCard 
            title="Trading Days" 
            value={challenge.trading_days}
            subValue="No minimum required"
            icon={Calendar}
            color="gray"
          />
        </motion.div>

        {/* Match-Trader Credentials Section */}
        {(challenge.matchtrade_login || challenge.broker_platform === 'match_trader') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <MatchTraderCredentials
              credentials={{
                login: challenge.matchtrade_login,
                password: challenge.matchtrade_password,
                server: 'demo.match-trader.com',
                platform_url: 'https://trade.match-trader.com'
              }}
              accountStatus={challenge.status}
              showTitle={true}
            />
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Progress Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Profit Target Progress */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                  <Target className="w-5 h-5 text-cyan-400" />
                  Profit Target Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Target: {profitTarget}%</span>
                    <span className={challenge.profit_loss_percent >= 0 ? 'text-green-400' : 'text-red-400'}>
                      Current: {formatPercent(challenge.profit_loss_percent)}
                    </span>
                  </div>
                  <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                      style={{ width: `${Math.max(0, Math.min(progressToTarget, 100))}%` }}
                    />
                  </div>
                  {challenge.profit_loss_percent >= profitTarget && (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-4 h-4" />
                      <span>Target reached! {challenge.current_phase === 1 ? 'Phase 2 will begin.' : 'Ready for funding!'}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Drawdown Progress */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Drawdown Used
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Max Drawdown: 10%</span>
                    <span className={drawdownUsed > 80 ? 'text-red-400' : 'text-gray-400'}>
                      Used: {drawdownUsed.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        drawdownUsed > 80 ? 'bg-gradient-to-r from-red-500 to-rose-500' : 
                        drawdownUsed > 50 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 
                        'bg-gradient-to-r from-gray-500 to-gray-400'
                      }`}
                      style={{ width: `${Math.min(drawdownUsed, 100)}%` }}
                    />
                  </div>
                  {drawdownUsed >= 100 && (
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Maximum drawdown reached. Challenge failed.</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Floating Loss Indicator - 2% Rule */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                  <TrendingDown className="w-5 h-5 text-orange-400" />
                  Floating Loss (2% Rule)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FloatingLossIndicator 
                  floatingLossPercent={challenge.floating_loss_percent || 0}
                  maxFloatingLoss={challenge.max_floating_loss || 2}
                  size="large"
                />
                <p className="text-xs text-gray-500 mt-3">
                  Your account will be automatically breached if floating loss exceeds the limit at any time during trading.
                </p>
              </CardContent>
            </Card>

            {/* Rules Reminder */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  Challenge Rules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <div className="w-8 h-8 rounded bg-cyan-500/20 flex items-center justify-center mt-0.5">
                      <Target className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Profit Target</p>
                      <p className="text-sm text-gray-400">Phase 1: {challenge.profit_target_phase1 || 10}% • Phase 2: {challenge.profit_target_phase2 || 5}%</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <div className="w-8 h-8 rounded bg-red-500/20 flex items-center justify-center mt-0.5">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Max Drawdown</p>
                      <p className="text-sm text-gray-400">{challenge.max_total_loss || 8}% total • {challenge.max_daily_loss || 4}% daily</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center mt-0.5">
                      <Clock className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Time Limit</p>
                      <p className="text-sm text-gray-400">No time limit</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <div className="w-8 h-8 rounded bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <DollarSign className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Profit Split</p>
                      <p className="text-sm text-gray-400">Up to 100% when funded</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sidebar - Quick Trade Simulator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Trade Simulator */}
            {(challenge.status === 'active' || challenge.status === 'passed_phase1') && (
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardHeader>
                  <CardTitle className="font-heading text-lg uppercase text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    Quick Trade Simulator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-400">
                    Simulate a trade to test your strategy (for demo purposes).
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="tradeAmount" className="text-gray-300">Trade Amount ($)</Label>
                    <Input
                      id="tradeAmount"
                      type="number"
                      placeholder="Enter amount"
                      value={tradeAmount}
                      onChange={(e) => setTradeAmount(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      data-testid="trade-amount-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      onClick={() => handleSimulateTrade(true)}
                      disabled={simulating}
                      className="bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
                      data-testid="win-trade-btn"
                    >
                      {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
                      WIN
                    </Button>
                    <Button 
                      onClick={() => handleSimulateTrade(false)}
                      disabled={simulating}
                      className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      data-testid="lose-trade-btn"
                    >
                      {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                      LOSS
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Info */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase text-white">Account Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Account Type</span>
                  <span className="text-white font-medium">{challenge.package_type === 'instant' ? 'Instant Funding' : 'Evaluation'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Starting Balance</span>
                  <span className="text-white font-mono">{formatCurrency(challenge.starting_balance)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Profit Split</span>
                  <span className="text-cyan-400 font-medium">{challenge.profit_split || 80}%</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-400">Status</span>
                  {getStatusBadge(challenge.status)}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-white/10 text-white hover:bg-white/5"
                  onClick={() => navigate('/stats')}
                >
                  <BarChart3 className="w-4 h-4 mr-2 text-cyan-400" />
                  View Performance
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-white/10 text-white hover:bg-white/5"
                  onClick={() => navigate('/analysis')}
                >
                  <Activity className="w-4 h-4 mr-2 text-blue-400" />
                  Market Signals
                </Button>
                {challenge.status === 'funded' && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start border-white/10 text-white hover:bg-white/5"
                    onClick={() => navigate('/certificates')}
                  >
                    <Award className="w-4 h-4 mr-2 text-yellow-400" />
                    Download Certificate
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeDetail;
